
import { useState, useCallback } from 'react';
import { mobileSupabase as supabase } from '@/integrations/supabase/mobile-client';
import { Family, FamilyMember } from '../types';
import { logger } from '@/utils/logger';

export const useFetchFamilyData = (
  userId: string | undefined,
  onError?: (error: string) => void
) => {
  const [isLoading, setIsLoading] = useState(true);
  const [family, setFamily] = useState<Family | null>(null);
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchFamilyData = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      logger.log('Fetching family data for user:', userId);
      
      // First check if user is a family owner with a direct query
      const { data: ownedFamily, error: ownedError } = await supabase
        .from('families')
        .select('*')
        .eq('created_by', userId)
        .maybeSingle();
      
      if (ownedError) {
        logger.error('Error checking owned family:', ownedError);
        setError(`Failed to check family ownership: ${ownedError.message}`);
        if (onError) onError(`Failed to check family ownership: ${ownedError.message}`);
        setIsLoading(false);
        return;
      }
      
      if (ownedFamily) {
        logger.log('User is family owner:', ownedFamily);
        setFamily(ownedFamily);
        
        // Fetch family members with direct query
        const { data: membersData, error: membersError } = await supabase
          .from('family_members')
          .select('*')
          .eq('family_id', ownedFamily.id);
        
        if (membersError) {
          logger.error('Error fetching family members:', membersError);
          setError(`Failed to fetch family members: ${membersError.message}`);
          if (onError) onError(`Failed to fetch family members: ${membersError.message}`);
        } else if (membersData) {
          logger.log('Family members:', membersData);
          setMembers(membersData as FamilyMember[]);
        }
        
        setIsLoading(false);
        return;
      }
      
      // If not an owner, check for membership with direct query
      const { data: memberData, error: membershipError } = await supabase
        .from('family_members')
        .select('family_id')
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();
      
      if (membershipError) {
        logger.error('Error fetching family membership:', membershipError);
        setError(`Failed to check family membership: ${membershipError.message}`);
        if (onError) onError(`Failed to check family membership: ${membershipError.message}`);
        setIsLoading(false);
        return;
      }
      
      if (memberData) {
        logger.log('User is a member of family:', memberData.family_id);
        
        // Fetch family details with direct query
        const { data: familyData, error: familyError } = await supabase
          .from('families')
          .select('*')
          .eq('id', memberData.family_id)
          .maybeSingle();
        
        if (familyError) {
          logger.error('Error fetching family:', familyError);
          setError(`Failed to fetch family details: ${familyError.message}`);
          if (onError) onError(`Failed to fetch family details: ${familyError.message}`);
        } else if (familyData) {
          setFamily(familyData);
          
          // Fetch family members with direct query
          const { data: membersData, error: membersError } = await supabase
            .from('family_members')
            .select('*')
            .eq('family_id', familyData.id);
          
          if (membersError) {
            logger.error('Error fetching family members:', membersError);
            setError(`Failed to fetch family members: ${membersError.message}`);
            if (onError) onError(`Failed to fetch family members: ${membersError.message}`);
          } else if (membersData) {
            setMembers(membersData as FamilyMember[]);
          }
        }
      } else {
        logger.log('User has no family');
        setFamily(null);
        setMembers([]);
      }
    } catch (error: unknown) {
      logger.error('Error in fetchFamilyData:', error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      const errorMessage = `An unexpected error occurred: ${message}`;
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [userId, onError]);

  return {
    isLoading,
    family,
    members,
    error,
    fetchFamilyData,
  };
};
